import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/design-system/button';
import { Paginator } from '@/design-system/pagination';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';
import {
  useDebouncedValue,
  useUrlNumber,
  useUrlParam,
  useUrlPatch,
} from '@/hooks/urlState';
import {
  deliveryDetailUrl,
  backOfficeDriverUrl,
  COD_SORT_FIELDS,
  type CodOrderListResult,
  type CodOrderRow,
  type CodSortDir,
  type CodSortField,
  type CodReconRiderRow,
  getCodOrders,
  logCodContact,
  riderWhatsAppUrl,
} from '@/services/api/codOrders';
import CodFilters, {
  DATE_PRESETS,
  rangeForPreset,
  type DatePreset,
} from './CodFilters';
import { CodBulkLeg1Modal, CodBulkLeg2Modal } from './CodBulkLegModals';
import CodBulkReconPanel from './CodBulkReconPanel';
import CodFinancePanel from './CodFinancePanel';
import { CodFinanceBoundary } from './CodFinanceBoundary';
import CodBulkRiderPanel from './CodBulkRiderPanel';
import CodOrderDetailPanel from './CodOrderDetailPanel';
import CodSummaryCards, {
  ALL_BUCKETS,
  BUCKET_FILTERS,
  type BucketFilter,
} from './CodSummaryCards';
import { downloadCodCsv } from './codExport';
import {
  CodConfirmationCell,
  CodDepositCell,
  CodResolvedCell,
  CodStatusBadge,
  formatDateTime,
  formatRupiah,
  formatTimeInStatus,
} from './CodBadges';

/** Date-range URL keys, shared by the read hooks and the combined write. */
const RANGE_KEY = 'range';
const RANGE_FROM_KEY = 'from';
const RANGE_TO_KEY = 'to';
/**
 * The date filter's "unset" preset. `all`, not a window: the only date the
 * board filters on is the completion date, and a range excludes every row
 * without one — defaulting to 30 days would open the page with the in-delivery,
 * failed and returned cards permanently at zero.
 */
const DEFAULT_RANGE: DatePreset = 'all';

/**
 * The list's default order: latest completed first. The server falls back to
 * the same thing, but it is explicit here so the Tanggal Selesai header shows
 * the arrow on first load — an ordering the operator cannot see is one they
 * cannot tell apart from none.
 */
const DEFAULT_SORT_BY: CodSortField = 'completedAt';
const DEFAULT_SORT_DIR: CodSortDir = 'desc';
const SORT_DIRS: readonly CodSortDir[] = ['asc', 'desc'];

const EMPTY_SUMMARY: CodOrderListResult['summary'] = {
  total: { count: 0, amount: 0 },
  inDelivery: { count: 0, amount: 0 },
  outstanding: { count: 0, amount: 0 },
  pendingDeposit: { count: 0, amount: 0 },
  pendingFinance: { count: 0, amount: 0 },
  confirmed: { count: 0, amount: 0 },
  failedDelivery: { count: 0, amount: 0 },
  notApplicable: { count: 0, amount: 0 },
};

/** Buckets where a PIC still owes an action — where Confirm belongs on the row. */
const ACTIONABLE = new Set(['outstanding', 'pending_deposit']);

/** The two views of the same ledger. Same shape the Invoices menu uses. */
const VIEWS = ['board', 'bulk', 'finance'] as const;
type CodView = (typeof VIEWS)[number];

/**
 * COD Reconciliation — where every Cash-on-Delivery order's money is.
 *
 * Distinct from Shipments, which answers where the GOODS are: an order can be
 * delivered while the cash sits in the rider's pocket for another day, and this
 * screen exists for that gap.
 *
 * Five buckets, not the mockup's eight. `escalated`, `deposit_overdue`,
 * `needs_attention` and `unreachable` all need an ageing threshold Ops and
 * Finance have not agreed, and the one the PRD assumed (24h) is contradicted by
 * production — the real median is 31.1h, so those tiers would paint over half
 * the list red on day one. The table sorts latest-completed-first and shows
 * elapsed time per row, so age stays visible without inventing a threshold; the bucket
 * cards are how the overdue tail gets found.
 */
const CodOrdersPage: React.FC = () => {
  const [result, setResult] = useState<CodOrderListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  /**
   * Which order's detail is open, in the URL.
   *
   * In the URL and not in state because the rider recap now links INTO this
   * panel: a link needs an address. It also makes a detail shareable, which
   * ops asked for by pasting screenshots instead.
   *
   * Keyed by shipment id, not by booking id or delivery uid, even though those
   * are what the link shows. A booking id can map to more than one delivery
   * (DHL-IOH does), so resolving one would sometimes open the wrong sibling —
   * and the panel fetches by shipment id anyway. `''` is closed; writing the
   * fallback deletes the param.
   *
   * `push`, so Back closes the panel instead of leaving the page.
   */
  const [selectedId, setSelectedId] = useUrlParam<string>('order', '', {
    history: 'push',
  });
  const [confirmFor, setConfirmFor] = useState<string | null>(null);

  const [bucket, setBucket] = useUrlParam<BucketFilter>('bucket', ALL_BUCKETS, {
    allowed: BUCKET_FILTERS,
    resetPage: true,
  });
  const [search, setSearch] = useUrlParam<string>('search', '', {
    resetPage: true,
  });
  const [clientId, setClientId] = useUrlNumber('clientId', 0, { min: 0 });
  // Read-only, like `from` / `to` below: all three are written together in the
  // single patch inside the filter's onChange.
  const [preset] = useUrlParam<DatePreset>(RANGE_KEY, DEFAULT_RANGE, {
    allowed: DATE_PRESETS,
  });
  const [customStart] = useUrlParam<string>(RANGE_FROM_KEY, '');
  const [customEnd] = useUrlParam<string>(RANGE_TO_KEY, '');
  const { patch } = useUrlPatch();
  // Read-only: the view is written by `switchView`, which has to clear every
  // filter in the SAME `patch` — a `setView` call would be a second navigate
  // and would discard that reset.
  const [view] = useUrlParam<CodView>('view', 'board', {
    allowed: VIEWS,
    history: 'push',
  });
  /**
   * Which rider is open, and on which surface — the read-only detail, or one
   * of the two leg forms.
   *
   * Not in the URL: these are transient actions, and a shared link should not
   * re-open a money form. One piece of state rather than three booleans, so
   * two surfaces can never be open at once — closing a leg modal returns to
   * whatever opened it, and `detail` is what the row click sets.
   */
  const [bulkView, setBulkView] = useState<{
    rider: CodReconRiderRow;
    surface: 'detail' | 'leg1' | 'leg2';
  } | null>(null);
  /** Bumped after a successful bulk so the panel refetches — the groups it is
   *  showing have just changed underneath it. */
  const [bulkToken, setBulkToken] = useState(0);
  /**
   * Sorting lives in the URL, like every other filter here.
   *
   * Server-side, delegated: this table is paginated, so a browser-side sort
   * would order the 20 rows on screen and call it the top of the list. The URL
   * carries it so a sorted view is shareable and survives a reload, and
   * `resetPage` drops `page` — the row that was on page 3 of one ordering is
   * not on page 3 of another.
   */
  // Read-only here on purpose: `sortBy` and `sortDir` are always written
  // TOGETHER in one `patch`, because `setSearchParams` is a `navigate()` and
  // does not queue — two setter calls in one event both read the pre-update
  // location and the second discards the first. Same trap the date range hit.
  const [sortBy] = useUrlParam<CodSortField>('sortBy', DEFAULT_SORT_BY, {
    allowed: COD_SORT_FIELDS,
    resetPage: true,
  });
  const [sortDir] = useUrlParam<CodSortDir>('sortDir', DEFAULT_SORT_DIR, {
    allowed: SORT_DIRS,
    resetPage: true,
  });
  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [limit, setLimit] = useUrlNumber('limit', 20, {
    min: 1,
    resetPage: true,
  });

  const debouncedSearch = useDebouncedValue(search);

  // 0 is the "unset" sentinel: `useUrlNumber` needs a numeric fallback, and no
  // real provider has id 0.
  const effectiveClientId = clientId || undefined;

  const params = useMemo(
    () => ({
      bucket: bucket === ALL_BUCKETS ? undefined : bucket,
      sortBy,
      sortDir,
      search: debouncedSearch || undefined,
      clientId: effectiveClientId,
      // The completion pair, not `startDate` / `endDate`: the board filters on
      // when the delivery completed. Mapped here rather than inside
      // `rangeForPreset`, so that helper keeps computing dates and knows nothing
      // about API names.
      ...(() => {
        const range = rangeForPreset(preset, {
          start: customStart,
          end: customEnd,
        });
        return {
          completedStartDate: range.startDate,
          completedEndDate: range.endDate,
        };
      })(),
    }),
    [
      bucket,
      sortBy,
      sortDir,
      debouncedSearch,
      effectiveClientId,
      preset,
      customStart,
      customEnd,
    ]
  );

  // Board only. The recap and Finance tabs render neither the rows nor the
  // cards, so fetching them there costs a paginated shipments query plus the
  // summary's seven `count(*) FILTER` pairs for a result nothing reads — and
  // repeats it on every filter change.
  const needsBoardData = view === 'board';

  const load = useCallback(async () => {
    if (!needsBoardData) return;
    setLoading(true);
    try {
      setResult(await getCodOrders({ ...params, page, limit }));
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [params, page, limit, needsBoardData]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = result?.summary ?? EMPTY_SUMMARY;
  const rows = result?.rows ?? [];
  const total = result?.total ?? 0;

  /**
   * After a bulk lands, BOTH views are stale: the rider's group shrank (or
   * moved to the other stage) and the board's cards moved. Closing back to
   * nothing rather than to the detail panel, because the panel's own figures
   * came from the row that just changed.
   */
  /**
   * The filters every bulk surface shares — the board's scope minus `bucket`,
   * which the bulk view fixes server-side.
   *
   * Derived once and passed down rather than re-picked per surface: the table
   * row, the rider panel and both leg modals each fetch their own rows, and if
   * any of them used a different scope its count would disagree with the row
   * the operator clicked.
   */
  const bulkScope = useMemo(
    () => ({
      clientId: params.clientId,
      search: params.search,
      // The same completion range the bar shows on this tab — a filter that is
      // on screen but not applied is the bug this replaced.
      completedStartDate: params.completedStartDate,
      completedEndDate: params.completedEndDate,
    }),
    [
      params.clientId,
      params.search,
      params.completedStartDate,
      params.completedEndDate,
    ]
  );

  /**
   * Switch view and CLEAR EVERY FILTER.
   *
   * Sharing them was the original intent — "the two views cannot disagree while
   * a filter is on" — but the two views do not ask the same question, and one
   * of the params could not be shared at all: `bucket` does not exist in the
   * per-rider view (it fixes both active buckets server-side), so carrying it
   * across meant selecting Outstanding on the board, seeing the rider view
   * ignore it, and coming back to a board still filtered by something the
   * other tab had appeared to drop. A search term has the same problem in
   * reverse: an order id filters rider GROUPS, which is a question nobody
   * asked. Sort is board-only, and its field names do not exist on the other
   * side at all.
   *
   * `limit` survives: page size is a display preference, not a filter.
   *
   * ONE `patch`, not a sequence of setters. `setSearchParams` is a
   * `navigate()` underneath and does not queue — two calls in the same event
   * both read the pre-update location and the second silently discards the
   * first. That is the trap the date range already hit once.
   */
  const switchView = useCallback(
    (next: CodView) => {
      patch(
        (params) => {
          if (next === 'board') params.delete('view');
          else params.set('view', next);
          for (const key of [
            'search',
            'clientId',
            'bucket',
            RANGE_KEY,
            RANGE_FROM_KEY,
            RANGE_TO_KEY,
            'sortBy',
            'sortDir',
            'page',
            // The open detail belongs to the view being left. Left behind, it
            // would hang over the other tab with no row to have opened it.
            'order',
          ]) {
            params.delete(key);
          }
        },
        // Push, so Back returns to the view you came from WITH the filters you
        // had — the reset is a step forward, not a destroyed history entry.
        'push'
      );
    },
    [patch]
  );

  const onBulkDone = useCallback(() => {
    setBulkView(null);
    setBulkToken((n) => n + 1);
    void load();
  }, [load]);

  const onExport = useCallback(async () => {
    setExporting(true);
    try {
      await downloadCodCsv(params, total);
      toast.success('CSV terunduh.');
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setExporting(false);
    }
  }, [params, total]);

  const columns = useMemo<TableDataColumn<CodOrderRow>[]>(
    () => [
      {
        key: 'rider',
        sortable: true,
        title: 'Rider',
        render: (_v, row) => {
          const url = backOfficeDriverUrl(row.riderId);
          // Linked on the name only, with the code as quiet context beneath —
          // the operator recognises the code, but it is the id that addresses
          // the page. No id (a snapshot from before the column existed) means
          // plain text rather than a link that 404s.
          const label = url ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-accent hover:text-accent-dark hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.riderName ?? row.riderCode}
            </a>
          ) : (
            <span>{row.riderName ?? '—'}</span>
          );
          return row.riderName || row.riderCode ? (
            <div className="flex flex-col">
              {label}
              {row.riderCode && (
                <span className="text-xs text-text-sub-600">
                  {row.riderCode}
                </span>
              )}
            </div>
          ) : (
            // Null here is meaningful, not cosmetic: the delivery never passed
            // through a logistic dispatch AND no delivery event carried a
            // driver — nobody is on record as having collected this cash.
            <span className="text-text-sub-600">Belum ada rider</span>
          );
        },
      },
      {
        key: 'bookingId',
        sortable: true,
        title: 'Booking ID',
        render: (_v, row) => {
          const url = deliveryDetailUrl(row.deliveryUid);
          // The client's own reference. Linked to the back-office delivery page
          // because that is where an operator goes to see what actually
          // happened — a different app, so a new tab keeps this list open
          // behind it. Plain text when there is no delivery to link to.
          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              // `text-accent` (Dash Purple), NOT `text-primary-base` — that
              // token resolves to `--dash-gray-950`, so the link rendered
              // near-black and read as ordinary table text with no hint it
              // opened anything.
              className="font-medium text-accent hover:text-accent-dark hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.bookingId}
            </a>
          ) : (
            <span className="font-medium">{row.bookingId}</span>
          );
        },
      },
      {
        key: 'deliveryUid',
        title: 'Delivery ID',
        render: (_v, row) => {
          const url = deliveryDetailUrl(row.deliveryUid);
          if (!row.deliveryUid) {
            // Null is information, not a gap: the shipment never reached
            // delivery-service, so there is no delivery to open.
            return <span className="text-text-sub-600">—</span>;
          }
          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:text-accent-dark hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.deliveryUid}
            </a>
          ) : (
            <span>{row.deliveryUid}</span>
          );
        },
      },
      {
        key: 'client',
        title: 'Client / Hub',
        // Capped, then WRAPPED — not truncated. The cap is still needed: a
        // client name like "Agri Makmur Nusantara Sejahtera Sehat Sentosa"
        // stretched this column to 348px and pushed the stage columns
        // off-screen. But `truncate` on top of the cap cut that same name (and
        // "Dash Hub Kota Bekasi - Pondok Ungu") at 1024, the smallest desktop
        // we support, leaving the operator a tooltip they have to hover to
        // read. Two lines costs a little row height and reads at a glance.
        render: (_v, row) => (
          // `whitespace-normal` is load-bearing: the design system's table
          // cell sets `white-space: nowrap`, which beats `break-words` and is
          // why wrapping alone did nothing here.
          <div className="flex max-w-[190px] flex-col whitespace-normal">
            <span className="break-words">{row.clientName ?? '—'}</span>
            <span className="break-words text-xs text-text-sub-600">
              {row.hubName ?? '—'}
            </span>
          </div>
        ),
      },
      {
        key: 'owner',
        title: 'Owner (PIC)',
        // The whole address, not the local part. `cod_owner` is an audit
        // actor — the same value as `confirmed_by` and the Finance allowlist —
        // so a cut-off one matches nothing when copied out of here.
        render: (_v, row) =>
          row.owner ? (
            <span className="block max-w-[220px] truncate" title={row.owner}>
              {row.owner}
            </span>
          ) : (
            <span className="text-text-sub-600">—</span>
          ),
      },
      {
        key: 'completedAt',
        sortable: true,
        title: 'Tanggal Selesai',
        // Kapan pengirimannya berhasil — momen uang jadi tanggungan seseorang.
        // Sengaja BERSEBELAHAN dengan "Time in Status", karena keduanya sering
        // disalahartikan sebagai hal yang sama: yang ini titik tetap, yang itu
        // jam berjalan yang di-reset tiap tahap. Berdampingan, bedanya terbaca
        // sendiri tanpa perlu dijelaskan.
        //
        // "—" untuk pesanan yang belum selesai, yang retur/gagal kirim, dan
        // yang selesai sebelum jejak auditnya ada — bukan ditebak dari kolom
        // lain yang kebetulan berisi tanggal.
        render: (_v, row) =>
          row.completedAt ? (
            <span className="whitespace-nowrap text-sm">
              {formatDateTime(row.completedAt)}
            </span>
          ) : (
            <span className="text-text-sub-600">—</span>
          ),
      },
      {
        key: 'stageStartedAt',
        sortable: true,
        title: 'Time in Status',
        align: 'right',
        render: (_v, row) => formatTimeInStatus(row.stageStartedAt, row.status),
      },
      {
        key: 'amount',
        sortable: true,
        title: 'Amount',
        align: 'right',
        render: (_v, row) => (
          <span className="font-medium tabular-nums">
            {formatRupiah(row.amount)}
          </span>
        ),
      },
      {
        key: 'status',
        sortable: true,
        title: 'Status',
        // Just the bucket. The proof and discrepancy flags moved to the
        // Confirmation column, where the thing they describe lives.
        render: (_v, row) => <CodStatusBadge status={row.status} />,
      },
      {
        // The mockup's stage columns, in custody order. Placed after Status
        // rather than replacing it: Status names the bucket a row sits in,
        // these three say what actually happened at each handoff — and the two
        // buckets with no handoff at all (in-delivery, returned) only have an
        // answer in Status.
        key: 'confirmation',
        title: 'Confirmation (Rider → PIC)',
        render: (_v, row) => <CodConfirmationCell row={row} />,
      },
      {
        key: 'deposit',
        title: 'Deposit (PIC → Finance)',
        render: (_v, row) => <CodDepositCell row={row} />,
      },
      {
        key: 'resolved',
        title: 'Resolved',
        render: (_v, row) => <CodResolvedCell row={row} />,
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        sticky: 'right',
        render: (_v, row) => {
          const wa = riderWhatsAppUrl(row.riderPhone);
          const actionable = row.status && ACTIONABLE.has(row.status);
          return (
            <div className="flex items-center justify-end gap-1">
              {/* Chasing the rider is the action this screen exists to prompt,
                  so it lives on the row rather than behind a drawer open.
                  Disabled rather than hidden when there is no number — the
                  absence is information: nobody has a way to reach them. */}
              <Button
                size="xs"
                style="stroke"
                tone="neutral"
                disabled={!wa}
                title={
                  wa
                    ? `Chat ${row.riderName ?? 'rider'} di WhatsApp`
                    : 'Nomor rider tidak tersedia'
                }
                onClick={(event) => {
                  event.stopPropagation();
                  if (!wa) return;
                  window.open(wa, '_blank', 'noopener,noreferrer');
                  // Logged after the window opens and deliberately not awaited:
                  // the chase is the point, and a failed audit write must never
                  // be the reason a PIC could not reach a rider. Swallowed for
                  // the same reason — a toast about a timeline entry would be
                  // noise on top of an action that already succeeded.
                  void logCodContact(row.id, { stage: row.status }).catch(
                    () => undefined
                  );
                }}
              >
                WhatsApp
              </Button>
              {actionable && (
                <Button
                  size="xs"
                  onClick={(event) => {
                    event.stopPropagation();
                    setConfirmFor(row.id);
                  }}
                >
                  {row.status === 'outstanding' ? 'Confirm' : 'Deposit'}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-medium text-text-strong-950">
          COD Reconciliation
        </h1>
        {/* M4 — the subtitle described the board's sort order on both views.
            The bulk view sorts by total held, descending, so the sentence was
            simply wrong for half the page. */}
        <p className="text-sm text-text-sub-600">
          {view === 'bulk'
            ? 'Rekap COD belum selesai per rider. Diurutkan dari yang memegang paling banyak.'
            : view === 'finance'
              ? 'Antrian verifikasi Finance, dikelompokkan per setoran — satu setoran PIC = satu baris mutasi bank.'
              : 'Posisi uang setiap pesanan COD, dari rider sampai Finance. Diurutkan dari yang terakhir selesai.'}
        </p>
      </div>

      <div className="flex items-center">
        <SegmentedControl
          value={view}
          // Radix single-toggle emits "" when the active item is re-clicked,
          // which would deselect both segments and render neither view.
          onValueChange={(v: string) => {
            if (v && v !== view) switchView(v as CodView);
          }}
        >
          <SegmentedItem value="board">Status board</SegmentedItem>
          <SegmentedItem value="bulk">Rekap per rider</SegmentedItem>
          <SegmentedItem value="finance">Finance</SegmentedItem>
        </SegmentedControl>
      </div>

      {/* The cards select a bucket, and the bulk view has no bucket to select
          — it fixes the two active ones. Showing them there would offer a
          control that does nothing. */}
      {view === 'board' && (
        <CodSummaryCards
          summary={summary}
          selected={bucket}
          onSelect={setBucket}
          // Only the FIRST load shows skeletons. Re-filtering keeps the
          // previous figures on screen instead of blanking the row the
          // operator just clicked, which would make the cards flicker on
          // every selection.
          loading={loading && result === null}
        />
      )}

      {/* Not on Finance: that tab filters by client and hub through its own
          controls, and none of search / client / date range reach it. Leaving
          the bar there offered an operator four inputs that changed the URL and
          nothing on screen. */}
      {view !== 'finance' && (
        <CodFilters
          value={{
            search,
            clientId: effectiveClientId,
            completed: { preset, customStart, customEnd },
          }}
          onChange={(next) => {
            if (next.search !== undefined) setSearch(next.search);
            if (next.clientId !== undefined) setClientId(next.clientId ?? 0);

            // A range's preset, from and to are written in ONE patch, never as
            // separate setter calls. `setSearchParams` is a `navigate()`
            // underneath, not a React state setter, so it does not queue: two
            // calls in the same event both read the pre-update location and the
            // second silently discards the first. Applying a custom range sets
            // all three at once, so this is the difference between one request
            // and a filter that half-applies.
            const range = next.completed;
            if (range) {
              patch((params) => {
                if (range.preset !== undefined) {
                  // Writing the default deletes the param, as useUrlParam does.
                  if (range.preset === DEFAULT_RANGE) params.delete(RANGE_KEY);
                  else params.set(RANGE_KEY, range.preset);
                }
                if (range.customStart !== undefined) {
                  if (range.customStart)
                    params.set(RANGE_FROM_KEY, range.customStart);
                  else params.delete(RANGE_FROM_KEY);
                }
                if (range.customEnd !== undefined) {
                  if (range.customEnd)
                    params.set(RANGE_TO_KEY, range.customEnd);
                  else params.delete(RANGE_TO_KEY);
                }
                // Same reason every other filter resets pagination: the row that
                // was on page 3 of the old range is not on page 3 of the new one.
                params.delete('page');
              });
            }
          }}
          onExport={onExport}
          exporting={exporting}
          exportCount={total}
          // Board only. The recap's rows are a per-rider aggregate with no CSV
          // writer behind them, and `total` counts board rows regardless of which
          // tab is showing.
          showExport={view === 'board'}
        />
      )}

      {view === 'finance' ? (
        // Its own filters, because Finance filters by client and hub rather
        // than by bucket or date, and its grouping is the deposit rather than
        // the order. Sharing the board's filter bar would offer controls that
        // mean nothing here.
        <CodFinanceBoundary>
          {/* Membuka detail COD lewat URL yang sama dengan board (`?order=`),
              bukan state lokal — panel detailnya memang dipasang di level
              halaman ini, dan satu alamat berarti barisnya bisa dibagikan. */}
          <CodFinancePanel onOpenOrder={setSelectedId} />
        </CodFinanceBoundary>
      ) : view === 'bulk' ? (
        <CodBulkReconPanel
          // The same scope the board is under, minus `bucket` — the bulk view
          // fixes the two active buckets server-side. Sharing the filter state
          // is what stops the two views disagreeing while a filter is on.
          params={bulkScope}
          // Both confirm actions live in the drawer footer now, so the table
          // has one job: pick a rider.
          onOpenRider={(rider) => setBulkView({ rider, surface: 'detail' })}
          refreshToken={bulkToken}
        />
      ) : (
        <>
          <TableData
            // Remounted per ordering, because the kit seeds its header arrows
            // from `sorting` ONCE and then tracks clicks itself. Its own cycle
            // (asc → desc → off) has no "off" here — off is completedAt desc —
            // so left alone it would hide the arrow on the default column's
            // first click while the rows stayed put. The key re-seeds the arrow
            // from the URL, which is the only state that matters.
            key={`${sortBy}:${sortDir}`}
            data={rows}
            columns={columns}
            loading={loading}
            sorting={{
              field: sortBy,
              direction: sortDir,
              // Delegated: the server orders, we just record the choice. Same
              // column flips direction; a new column starts ascending, as the
              // kit does. Landing back on the default writes nothing.
              onSort: (field) => {
                const nextDir: CodSortDir =
                  field === sortBy && sortDir === 'asc' ? 'desc' : 'asc';
                const isDefault =
                  field === DEFAULT_SORT_BY && nextDir === DEFAULT_SORT_DIR;
                patch((params) => {
                  if (isDefault) {
                    params.delete('sortBy');
                    params.delete('sortDir');
                  } else {
                    params.set('sortBy', field);
                    params.set('sortDir', nextDir);
                  }
                  params.delete('page');
                });
              },
            }}
            onRowClick={(row) => setSelectedId(row.id)}
          />

          <Paginator
            currentPage={page}
            totalItems={total}
            itemsPerPage={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
          />
        </>
      )}

      {bulkView?.surface === 'detail' && (
        <CodBulkRiderPanel
          key={String(bulkView.rider.riderId)}
          rider={bulkView.rider}
          scope={bulkScope}
          onClose={() => setBulkView(null)}
          onConfirmLeg1={() =>
            setBulkView({ rider: bulkView.rider, surface: 'leg1' })
          }
          onConfirmLeg2={() =>
            setBulkView({ rider: bulkView.rider, surface: 'leg2' })
          }
        />
      )}

      {/* Keyed by rider AND leg so a fresh form mounts every time. Reusing the
          instance would carry the previous selection and its pre-filled total
          into a form that writes money — the same hazard the board's detail
          panel is keyed against. */}
      {bulkView?.surface === 'leg1' && (
        <CodBulkLeg1Modal
          key={`leg1-${bulkView.rider.riderId}`}
          rider={bulkView.rider}
          scope={bulkScope}
          onClose={() => setBulkView(null)}
          onDone={onBulkDone}
        />
      )}

      {bulkView?.surface === 'leg2' && (
        <CodBulkLeg2Modal
          key={`leg2-${bulkView.rider.riderId}`}
          rider={bulkView.rider}
          scope={bulkScope}
          onClose={() => setBulkView(null)}
          onDone={onBulkDone}
        />
      )}

      {(selectedId || confirmFor) && (
        <CodOrderDetailPanel
          // Keyed by the order so React MOUNTS a fresh panel per shipment
          // instead of reusing the instance. Without it, `useState`
          // initialisers never re-run and the confirm modal opens carrying the
          // PREVIOUS order's settlement source and — the dangerous part —
          // its pre-filled amount, which an operator could submit against the
          // wrong shipment without noticing.
          // `||`, not `??`: `selectedId` is `''` when closed, never null, so
          // `??` would keep the empty string and the panel would fetch
          // `/v1/cod-orders/` with no id whenever a row's Confirm button
          // opened it.
          key={selectedId || (confirmFor as string)}
          shipmentId={selectedId || (confirmFor as string)}
          // Opening from a row's Confirm button jumps straight into the modal
          // instead of making the operator find the same button again inside
          // the drawer.
          autoConfirm={selectedId === ''}
          onClose={() => {
            setSelectedId('');
            setConfirmFor(null);
          }}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default CodOrdersPage;
